import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faSpinner, faCheckCircle, faArrowUp, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { AuthGuard, RoleGuard } from "../App"; // Assuming AuthGuard and RoleGuard exist
import { StandardURL } from "../App"; // Import StandardURL from App.js


// --- START: CommissionModal Component (Moved OUTSIDE CommissionsPage to prevent focus loss) ---
const CommissionModal = ({ isOpen, onClose, onSubmit, isEdit = false, formData, selectedCommission, handleChange, loadingCode }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all duration-300 scale-100">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {isEdit ? "Edit Commission Balances" : "Add New Commission"}
                    </h2>
                    {isEdit && <p className="text-sm text-gray-500 mt-1">Editing transaction code: <span className="font-semibold text-gray-800">{selectedCommission?.transaction_code}</span></p>}
                </div>
                <form onSubmit={onSubmit}>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {!isEdit && (
                            <>
                                <input
                                    type="text"
                                    name="transactioncode"
                                    value={formData.transactioncode}
                                    onChange={handleChange}
                                    placeholder="Transaction Code"
                                    className="col-span-1 border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                                <input
                                    type="number"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    placeholder="Amount (e.g., 1000)"
                                    className="col-span-1 border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                                <input
                                    type="text"
                                    name="propertyid"
                                    value={formData.propertyid}
                                    onChange={handleChange}
                                    placeholder="Property ID"
                                    className="col-span-1 border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                                <input
                                    type="text"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Description"
                                    className="col-span-1 border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="col-span-1 border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="running">Running</option>
                                    <option value="done">Done</option>
                                </select>
                            </>
                        )}

                        <input
                            type="number"
                            name="krarunning"
                            value={formData.krarunning}
                            onChange={handleChange}
                            placeholder="KRA Running"
                            className="col-span-1 border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                            required
                            disabled={loadingCode}
                        />
                        <input
                            type="number"
                            name="runningbalance"
                            value={formData.runningbalance}
                            onChange={handleChange}
                            placeholder="Running Balance"
                            className="col-span-1 border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                            required
                            disabled={loadingCode}
                        />
                    </div>
                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loadingCode}
                            className={`px-5 py-2 rounded-lg text-white font-semibold shadow-md transition duration-200 ${
                                loadingCode
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                            {loadingCode ? (
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                            ) : (
                                <FontAwesomeIcon icon={isEdit ? faEdit : faPlus} className="mr-2" />
                            )}
                            {isEdit ? "Update Balances" : "Create Commission"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
// --- END: CommissionModal Component ---


const CommissionsPage = () => {
    const [commissions, setCommissions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [loadingCode, setLoadingCode] = useState(null); // Used for status/delete action loading

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [commissionsPerPage] = useState(10); 

    const initialFormState = {
        transactioncode: "",
        amount: "",
        propertyid: "",
        description: "",
        status: "running", // Default status
        krarunning: "",
        runningbalance: "",
    };

    const [formData, setFormData] = useState(initialFormState);
    const [selectedCommission, setSelectedCommission] = useState(null);

    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    useEffect(() => {
        fetchCommissions();
    }, []);

    // 1. FETCH ALL COMMISSIONS (GET)
    const fetchCommissions = async () => {
        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/commissionlink/fetch/all`, 
                config
            );
            
            const responseData = response.data.response;

            // FIX: Ensure commissions state is ALWAYS an array for .slice() to work
            if (Array.isArray(responseData)) {
                setCommissions(responseData);
            } else if (responseData && typeof responseData === 'object') {
                // If the API returns a single commission object, wrap it in an array
                setCommissions([responseData]);
            } else {
                // Default to an empty array for null, undefined, or unexpected values
                setCommissions([]);
            }

            toast.success("Commissions fetched successfully!");
        } catch (error) {
            console.error("Error fetching commissions:", error);
            toast.error("Failed to fetch commissions.");
            setCommissions([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper for form changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // 2. ADD NEW COMMISSION (POST)
    const handleAddCommission = async (e) => {
        e.preventDefault();
        if (!token) return;

        try {
            const payload = {
                ...formData,
                amount: Number(formData.amount),
                krarunning: Number(formData.krarunning),
                runningbalance: Number(formData.runningbalance),
            };

            await axios.post(
                `${StandardURL}/iNyumba/commissionlink/post`,
                payload,
                config
            );
            toast.success("Commission created successfully!");
            setIsAddModalOpen(false);
            setFormData(initialFormState);
            fetchCommissions(); // Refresh the list
        } catch (error) {
            console.error("Error adding commission:", error);
            toast.error(error.response?.data?.response || "Failed to add commission.");
        }
    };

    // 3. EDIT COMMISSION (PATCH krarunning and runningbalance)
    const handleEditCommission = async (e) => {
        e.preventDefault();
        // Use the correct transaction code from the selected commission object
        const transactionCode = selectedCommission?.transaction_code;
        if (!token || !transactionCode) return;

        setLoadingCode(transactionCode);

        try {
            const payload = {
                krarunning: Number(formData.krarunning),
                runningbalance: Number(formData.runningbalance),
            };

            await axios.patch(
                `${StandardURL}/iNyumba/commissionlink/update/${transactionCode}`, 
                payload,
                config
            );
            toast.success("Commission updated successfully!");
            setIsEditModalOpen(false);
            setLoadingCode(null);
            fetchCommissions(); // Refresh the list
        } catch (error) {
            console.error("Error updating commission:", error);
            toast.error(error.response?.data?.response || "Failed to update commission.");
            setLoadingCode(null);
        }
    };

    // Function to open the Edit Modal with pre-populated data
    const openEditModal = (commission) => {
        setSelectedCommission(commission);
        setFormData({
            // Map snake_case keys from fetched object to camelCase form keys
            transactioncode: commission.transaction_code,
            amount: commission.amount,
            propertyid: commission.propertyid,
            description: commission.description,
            status: commission.status,
            krarunning: commission.kra_running,
            runningbalance: commission.running_balance,
        });
        setIsEditModalOpen(true);
    };

    // 4. TOGGLE STATUS (PATCH status)
    const handleToggleStatus = async (commission) => {
        const transactionCode = commission.transaction_code;
        if (!token) return;

        const newStatus = commission.status === "running" ? "done" : "running";
        const isConfirmed = window.confirm(
            `Are you sure you want to change status from '${commission.status}' to '${newStatus}' for ${transactionCode}?`
        );

        if (!isConfirmed) return;

        setLoadingCode(transactionCode);

        try {
            await axios.patch(
                `${StandardURL}/iNyumba/commissionlink/update/status/${transactionCode}`, 
                {},
                config
            );
            toast.success(`Status for ${transactionCode} changed to ${newStatus.toUpperCase()}!`);
            fetchCommissions(); // Refresh the list
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(error.response?.data?.response || "Failed to update status.");
        } finally {
            setLoadingCode(null);
        }
    };

    // 5. DELETE COMMISSION (DELETE)
    const handleDeleteCommission = async (transactionCode) => {
        if (!token) return;

        const isConfirmed = window.confirm(
            `Are you sure you want to delete the commission with code: ${transactionCode}?`
        );

        if (!isConfirmed) return;

        setLoadingCode(transactionCode);

        try {
            await axios.delete(
                `${StandardURL}/iNyumba/commissionlink/delete/${transactionCode}`, 
                config
            );
            toast.success(`Commission ${transactionCode} deleted successfully!`);
            fetchCommissions(); // Refresh the list
        } catch (error) {
            console.error("Error deleting commission:", error);
            toast.error(error.response?.data?.response || "Failed to delete commission.");
        } finally {
            setLoadingCode(null);
        }
    };

    // --- Pagination Logic ---
    const indexOfLastCommission = currentPage * commissionsPerPage;
    const indexOfFirstCommission = indexOfLastCommission - commissionsPerPage;
    const currentCommissions = commissions.slice(indexOfFirstCommission, indexOfLastCommission);
    const totalPages = Math.ceil(commissions.length / commissionsPerPage);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const PaginationControls = () => {
        if (totalPages <= 1) return null;

        return (
            <div className="flex justify-center mt-6">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} className="h-5 w-5" />
                    </button>
                    
                    {[...Array(totalPages).keys()].map(index => {
                        const pageNumber = index + 1;
                        const isCurrent = currentPage === pageNumber;

                        return (
                            <button
                                key={pageNumber}
                                onClick={() => paginate(pageNumber)}
                                className={`hidden md:inline-flex relative items-center px-4 py-2 border text-sm font-medium transition duration-150 ease-in-out ${
                                    isCurrent
                                        ? 'z-10 bg-blue-600 border-blue-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                {pageNumber}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <FontAwesomeIcon icon={faChevronRight} className="h-5 w-5" />
                    </button>
                </nav>
            </div>
        );
    };
    // -------------------------

    // Status Badge Component
    const StatusBadge = ({ status }) => {
        const isRunning = status === "running";
        const color = isRunning ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800";
        return (
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${color} capitalize`}>
                {status}
            </span>
        );
    };

    return (
        // <RoleGuard requiredRole="admin">
        <AuthGuard>
            <div className="container mx-auto px-4 py-8">
                <ToastContainer position="bottom-right" />
                
                <h1 className="text-4xl font-extrabold text-gray-900 mb-8 border-b-4 border-blue-600 pb-2">
                    Commissions Management
                </h1>

                {/* Header and Add Button */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-gray-700">All Commission Records</h2>
                    <button
                        onClick={() => {
                            setFormData(initialFormState);
                            setIsAddModalOpen(true);
                        }}
                        className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition duration-300 transform hover:scale-[1.02]"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add New Commission
                    </button>
                </div>

                {/* Commission Table */}
                <div className="bg-white shadow-2xl rounded-xl overflow-hidden">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-blue-600" />
                            <span className="ml-3 text-lg text-gray-600">Loading commissions...</span>
                        </div>
                    ) : commissions.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 text-lg">
                            No commission records found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {["Code", "Property ID", "Amount", "Description", "Status", "KRA Running", "Balance", "Actions"].map((header) => (
                                            <th
                                                key={header}
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {currentCommissions.map((commission) => (
                                        <tr key={commission.transaction_code} className="hover:bg-gray-50 transition duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {commission.transaction_code}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {commission.propertyid}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                KSh {Number(commission.amount).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-xs truncate">
                                                {commission.description}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge status={commission.status} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                                                KSh {Number(commission.kra_running).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                                                KSh {Number(commission.running_balance).toLocaleString()}
                                            </td>

                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    {/* Edit Button */}
                                                    <button
                                                        onClick={() => openEditModal(commission)}
                                                        disabled={loadingCode === commission.transaction_code}
                                                        className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-50 transition duration-150 disabled:opacity-50"
                                                        title="Edit Balances"
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </button>

                                                    {/* Status Toggle Button */}
                                                    <button
                                                        onClick={() => handleToggleStatus(commission)}
                                                        disabled={loadingCode === commission.transaction_code}
                                                        className={`p-2 rounded-full transition duration-150 disabled:opacity-50 ${
                                                            commission.status === 'running'
                                                                ? 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                                                : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'
                                                            }`}
                                                        title={commission.status === 'running' ? 'Mark as Done' : 'Mark as Running'}
                                                    >
                                                        {loadingCode === commission.transaction_code ? (
                                                            <FontAwesomeIcon icon={faSpinner} spin />
                                                        ) : commission.status === 'running' ? (
                                                            <FontAwesomeIcon icon={faCheckCircle} />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faArrowUp} />
                                                        )}
                                                    </button>

                                                    {/* Delete Button */}
                                                    <button
                                                        onClick={() => handleDeleteCommission(commission.transaction_code)}
                                                        disabled={loadingCode === commission.transaction_code}
                                                        className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition duration-150 disabled:opacity-50"
                                                        title="Delete Commission"
                                                    >
                                                        {loadingCode === commission.transaction_code ? (
                                                            <FontAwesomeIcon icon={faSpinner} spin />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                
                {/* Pagination Controls */}
                <PaginationControls />
            </div>

            {/* Add Commission Modal */}
            <CommissionModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddCommission}
                isEdit={false}
                formData={formData}
                selectedCommission={selectedCommission}
                handleChange={handleChange}
                loadingCode={loadingCode}
            />

            {/* Edit Commission Modal */}
            <CommissionModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedCommission(null);
                }}
                onSubmit={handleEditCommission}
                isEdit={true}
                formData={formData}
                selectedCommission={selectedCommission}
                handleChange={handleChange}
                loadingCode={loadingCode}
            />
        </AuthGuard>
        // </RoleGuard>
    );
};

export default CommissionsPage;